/**
 * 合成物品简介（ItemDef.desc / cfg_merge_item.desc_i18n）的长度口径。零依赖，两处共用这一份：
 * 发布闸 `game/merge-validate.ts`、管端物品对话框（`web/src/lib/merge.ts` 转出，`web/src/pages/MergePage.tsx` 用）。
 * 写接口 `admin/merge.ts` 不拦：合成块整块保存，库里一条老的超长简介会让所有物品都存不进去。
 *
 * **按显示宽度计，不按字符数**：全角字符（中日韩文字、假名、谚文、全角标点与全角字母）算 2，其余算 1，
 * 每种语言上限 30，也就是最多 15 个汉字或 30 个字母。
 * 依据：客户端底栏信息卡第二行只有一行、12 号字，最常见的「可售卖物品」那一列宽约 188px，
 * 放得下约 15 个汉字或约 30 个拉丁字母；更窄的情况客户端有 SHRINK 缩字兜底（用户 2026-10-26 定由管端限长）。
 *
 * 只认下面这张全角码位表；emoji、①、× 这类宽度不定的符号按 1 算。按码位数，代理对（扩展 B 区汉字）算一个字。
 * 宽度一律量 trim 之后的文本：写接口落库前会 trim（admin/merge.ts 的 cleanI18n），前后空白不占信息卡。
 */

/** 每种语言的简介最多占多宽（全角算 2） */
export const MERGE_DESC_MAX_WIDTH = 30;

/** 全角码位区间（闭区间）。取 Unicode East Asian Width 为 W / F 的主要区块，按起点升序 */
const WIDE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x1100, 0x115f], // 谚文字母（初声）
  [0x2e80, 0x303e], // CJK 部首、康熙部首、CJK 符号和标点（含全角空格、句号、书名号）
  [0x3041, 0x33ff], // 平假名、片假名、注音、谚文兼容字母、CJK 笔画、带圈 CJK、CJK 兼容
  [0x3400, 0x4dbf], // CJK 扩展 A
  [0x4e00, 0x9fff], // CJK 统一汉字
  [0xa000, 0xa4cf], // 彝文
  [0xa960, 0xa97f], // 谚文字母扩展 A
  [0xac00, 0xd7a3], // 谚文音节
  [0xf900, 0xfaff], // CJK 兼容汉字
  [0xfe10, 0xfe19], // 竖排标点
  [0xfe30, 0xfe6f], // CJK 兼容形式、小写变体
  [0xff00, 0xff60], // 全角 ASCII 与全角标点（半角片假名 FF61 起不算）
  [0xffe0, 0xffe6], // 全角符号（￥、￠ 等）
  [0x20000, 0x3fffd], // CJK 扩展 B 及以后（代理对）
];

function isWide(cp: number): boolean {
  for (const [lo, hi] of WIDE_RANGES) {
    if (cp < lo) return false;
    if (cp <= hi) return true;
  }
  return false;
}

/** 显示宽度：全角码位算 2，其余算 1 */
export function displayWidth(s: string): number {
  let w = 0;
  for (const ch of s) w += isWide(ch.codePointAt(0)!) ? 2 : 1;
  return w;
}

/** 一种语言的简介宽度（先 trim，与落库口径一致） */
export const descWidth = (s: string): number => displayWidth(s.trim());

export type DescWidthIssue = { locale: string; width: number; max: number };

/**
 * 超限的语言，按对象里的键序。null / 缺省 / 非字符串的值不算（形状由各自的调用方另判）。
 * 空数组 = 每种语言都不超。
 */
export function descWidthIssues(desc: Readonly<Record<string, unknown>> | null | undefined): DescWidthIssue[] {
  const out: DescWidthIssue[] = [];
  if (desc == null) return out;
  for (const [locale, text] of Object.entries(desc)) {
    if (typeof text !== "string") continue;
    const width = descWidth(text);
    if (width > MERGE_DESC_MAX_WIDTH) out.push({ locale, width, max: MERGE_DESC_MAX_WIDTH });
  }
  return out;
}
